import { useRef, useState } from 'react';

import { get, post } from 'helpers/api';
import { serverErrors } from 'helpers/api_errors';
import { useOneOffSubscription } from 'helpers/action_cable/use_one_off_subscription';

export type ComposerPhase = 'idle' | 'planning' | 'planned' | 'reviewing' | 'building' | 'failed' | 'replied';

export interface PlanPage {
  type: 'template_default' | 'library' | 'user_page' | 'ai_generated';
  refId?: number;
  name?: string;
  prompt?: string;
}

// Docs::DocumentPlan#to_h, camelized.
export interface DocumentPlan {
  baseTemplateId: number;
  contextType: string;
  name: string | null;
  pageSize: string;
  pages: PlanPage[];
  propertyId: number;
  version: number;
}

// The client may change only these; the server takes the rest from the stored plan.
export interface ReviewedPlan {
  name: string | null;
  pages: PlanPage[];
}

// GET /docs/ai_documents/:chat_id (Docs::AiDocumentReview), camelized.
export interface DocumentReview {
  // Pages not in the plan that it could take: left-out template defaults, then library and saved pages.
  addablePages: PlanPage[];
  plan: DocumentPlan;
  template: { id: number; name: string };
}

interface ComposerState {
  error: string | null;
  phase: ComposerPhase;
  reply: string | null;
  // The failed review or build never got a verdict (network, 5xx), so retrying it may work.
  retryable: boolean;
  status: string | null;
}

interface ToolCall {
  name: string;
  metadata?: { plan?: DocumentPlan } | null;
}

// ChatWorker's frames through Ai::Delivery::Cable. `status` carries the tool's progress label, and
// `done` ends the turn: its tool calls hold an accepted plan, or its content is the model's prose reply.
interface Frame {
  type?: 'status' | 'delta' | 'tool_calls' | 'done' | 'error';
  content?: string | null;
  error?: boolean;
  label?: string;
  toolCalls?: ToolCall[];
}

interface BuildResponse {
  documentId: number;
  editorPath: string;
}

export const GENERIC_ERROR = 'Something went wrong – try again.';

const IDLE: ComposerState = {
  error: null, phase: 'idle', reply: null, retryable: false, status: null,
};

// `done` is left to settle, so the idle timeout still covers a plan that waits on the create POST's reply.
const isTerminal = (frame: Frame) => frame.type === 'error' || !!frame.error;

const proposedPlan = (frame: Frame) => frame.toolCalls?.find(call => call.name === 'propose_plan')?.metadata?.plan;

async function createPlannerChat(propertyId: number, prompt: string, requestId: string) {
  const response = await post('/docs/ai_documents', {
    contextType: 'PropertyDocument', contextParams: { propertyId }, prompt, requestId,
  });
  return response.chatId as number;
}

// helpers/api rejects a 422 with its errors array of { code, message }.
const requestErrorMessage = (error: unknown) => {
  const messages = Array.isArray(error)
    ? error.map(entry => (typeof entry?.message === 'string' ? entry.message : null)).filter(Boolean)
    : [];

  return messages.join(' ') || GENERIC_ERROR;
};

// Only a request that never got a verdict (network, 5xx) is worth repeating; the build is idempotent per chat.
const isRetryableRequestError = (error: unknown) => {
  if (serverErrors(error).length > 0) return false;

  const status = (error as { response?: { status?: number } } | null)?.response?.status;
  return !status || status >= 500;
};

// Plans a property document from a prompt over OneOffChannel, then builds it and opens the editor.
// compose resolves true once a plan is ready to build, false when the planner replied, failed or was reset.
export function useDocumentComposer({ folderId, propertyId }: { folderId: number; propertyId: number }) {
  const { subscribe, unsubscribe } = useOneOffSubscription();
  const [state, setState] = useState<ComposerState>(IDLE);
  // Bumped to drop whatever the run before it still has in flight: frames, the create POST, a build.
  const runRef = useRef(0);
  const chatIdRef = useRef<number | null>(null);
  const settleRef = useRef<((planned: boolean) => void) | null>(null);

  function cancel() {
    runRef.current += 1;
    chatIdRef.current = null;
    unsubscribe();
    settleRef.current?.(false);
    settleRef.current = null;
  }

  function compose(prompt: string) {
    cancel();
    const run = runRef.current;
    const isCurrent = () => runRef.current === run;
    setState({ ...IDLE, phase: 'planning' });

    return new Promise<boolean>((resolve) => {
      settleRef.current = resolve;
      let created: Promise<number> | null = null;

      // The idle timeout can fail a run whose plan is still waiting on the chat id, so the first outcome wins.
      const settle = (next: ComposerState, planned = false) => {
        if (!isCurrent() || settleRef.current !== resolve) return;

        unsubscribe();
        settleRef.current = null;
        setState(next);
        resolve(planned);
      };
      const fail = () => settle({ ...IDLE, phase: 'failed', error: GENERIC_ERROR });

      const finish = async (frame: Frame) => {
        if (!proposedPlan(frame)) {
          if (frame.content) settle({ ...IDLE, phase: 'replied', reply: frame.content });
          else fail();
          return;
        }

        // The plan can beat the create POST's reply, and building needs the chat id that reply carries.
        try {
          const chatId = await created;
          if (isCurrent()) chatIdRef.current = chatId;
          settle({ ...IDLE, phase: 'planned' }, true);
        } catch {
          // connectedError has already failed the run.
        }
      };

      subscribe<Frame>(
        {
          async connected(requestId: string) {
            // ActionCable calls this again on a reconnect, which must not start a second chat.
            if (created) return;

            try {
              created = createPlannerChat(propertyId, prompt, requestId);
              const chatId = await created;
              if (isCurrent()) chatIdRef.current = chatId;
            } catch (error) {
              // Rethrowing makes useOneOffSubscription stop the subscription, which by now is a newer run's.
              if (isCurrent()) throw error;
            }
          },
          connectedError: fail,
          received(frame: Frame) {
            if (!isCurrent()) return;

            if (frame.error || frame.type === 'error') {
              fail();
            } else if (frame.type === 'status' && frame.label) {
              const { label } = frame;
              setState(current => ({ ...current, status: label }));
            } else if (frame.type === 'done') {
              void finish(frame);
            }
          },
          rejected: fail,
        },
        { isTerminal }
      );
    });
  }

  // The plan to review, re-validated by the server. A failure fails the run.
  async function fetchReview() {
    const run = runRef.current;
    setState(current => ({ ...current, error: null, phase: 'reviewing', retryable: false }));

    try {
      const review = await get(`/docs/ai_documents/${chatIdRef.current}`) as DocumentReview;
      if (runRef.current !== run) return null;

      setState(current => ({ ...current, phase: 'planned' }));
      return review;
    } catch (error) {
      if (runRef.current === run) {
        setState(current => ({
          ...current, error: requestErrorMessage(error), phase: 'failed', retryable: isRetryableRequestError(error),
        }));
      }
      return null;
    }
  }

  async function build(plan: ReviewedPlan) {
    const run = runRef.current;
    setState(current => ({ ...current, error: null, phase: 'building', retryable: false, status: null }));

    try {
      const { editorPath } = await post(
        `/docs/ai_documents/${chatIdRef.current}/build`, { folderId, plan }
      ) as BuildResponse;
      if (runRef.current === run) window.location.assign(editorPath);
    } catch (error) {
      if (runRef.current === run) {
        setState(current => ({
          ...current,
          error: requestErrorMessage(error),
          phase: 'failed',
          retryable: isRetryableRequestError(error),
        }));
      }
    }
  }

  function reset() {
    cancel();
    setState(IDLE);
  }

  return { ...state, build, compose, fetchReview, reset };
}
